import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowLeft } from 'lucide-react';
import { PlanModulesList } from '@/components/PlanModulesList';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/components/Toast';

export const PlansPage = () => {
  const toast = useToast();
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/saas/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    // Se não está logado, redireciona para landing com o plano selecionado
    if (!isAuthenticated) {
      navigate(`/?plan=${planId}`);
      return;
    }
    
    // Se está logado, assina o plano diretamente
    if (!window.confirm(`Deseja assinar o plano selecionado? Você terá 30 dias grátis para testar.`)) {
      return;
    }
    
    setSubscribing(planId);
    try {
      await api.post('/saas/subscribe', { plan_id: planId });
      toast.success('Plano ativado com sucesso! Você tem 30 dias grátis para testar.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao assinar plano');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Botão Voltar */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
        
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Escolha o Plano Ideal
          </h1>
          <p className="text-xl text-slate-600">
            Sistema PDV completo com emissão fiscal. Comece com 30 dias grátis!
          </p>
          {isAuthenticated && (
            <p className="text-sm text-emerald-600 mt-2">
              Logado como: {user?.email}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const isPopular = plan.id === 'professional' || plan.id === 'profissional';
            return (
              <Card
                key={plan.id}
                className={`relative ${
                  isPopular
                    ? 'border-amber-500 border-2 shadow-2xl transform scale-105'
                    : 'border-slate-200 shadow-lg'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      MAIS POPULAR
                    </span>
                  </div>
                )}

                <CardHeader className="text-center pb-8 pt-8">
                  <CardTitle className="text-2xl mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {formatCurrency(plan.price)}
                    </span>
                    <span className="text-slate-600">/mês</span>
                  </div>
                  <CardDescription className="mt-2">30 dias grátis para testar</CardDescription>
                </CardHeader>

                <CardContent>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Módulos liberados</p>
                  <PlanModulesList plan={plan} />
                </CardContent>

                <CardFooter>
                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={subscribing === plan.id}
                    className={`w-full h-12 ${
                      isPopular
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {subscribing === plan.id ? 'Processando...' : 'Começar Agora'}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          {!isAuthenticated && (
            <p className="text-slate-600">
              Já tem uma conta?{' '}
              <a href="/login" className="text-amber-600 font-semibold hover:underline">
                Fazer Login
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
